import React, { useState, useEffect } from 'react';
import {
  Mail,
  Plus,
  Edit,
  Trash2,
  Eye,
  Copy,
  Settings,
  FileText,
  Send,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Save,
  X,
  Code,
  Type,
  Globe,
  Users
} from 'lucide-react';
import { FirmAdminLayout } from './FirmAdminLayout';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface NotificationTemplate {
  _id: string;
  tenantId?: string | null;
  eventType: string;
  channel: string;
  name: string;
  description?: string;
  content: {
    subject?: string;
    body: string;
    htmlBody?: string;
    shortText?: string;
  };
  variables: Array<{
    name: string;
    description: string;
    type: string;
    required: boolean;
  }>;
  isActive: boolean;
  isDefault: boolean;
  language: string;
  level: 'platform' | 'tenant';
  version: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface TemplateVariable {
  name: string;
  description: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'object';
  required: boolean;
  defaultValue?: any;
}

export const NotificationTemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'preview'>('list');
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [availableVariables, setAvailableVariables] = useState<TemplateVariable[]>([]);
  const [activeEmailTab, setActiveEmailTab] = useState<'plain' | 'html'>('plain');

  const [templateForm, setTemplateForm] = useState({
    eventType: '',
    channel: 'email',
    name: '',
    description: '',
    content: {
      subject: '',
      body: '',
      htmlBody: '',
      shortText: ''
    },
    language: 'en',
    isActive: true
  });

  const eventTypes = [
    // Task Events
    { value: 'task_created', label: 'Task Created', category: 'Tasks' },
    { value: 'task_assigned', label: 'Task Assigned', category: 'Tasks' },
    { value: 'task_updated', label: 'Task Updated', category: 'Tasks' },
    { value: 'task_due_soon', label: 'Task Due Soon', category: 'Tasks' },
    { value: 'task_overdue', label: 'Task Overdue', category: 'Tasks' },
    { value: 'task_completed', label: 'Task Completed', category: 'Tasks' },
    { value: 'task_submitted', label: 'Task Submitted', category: 'Tasks' },
    { value: 'task_approved', label: 'Task Approved', category: 'Tasks' },
    { value: 'task_rejected', label: 'Task Rejected', category: 'Tasks' },
    { value: 'task_reallocated', label: 'Task Reallocated', category: 'Tasks' },
    { value: 'task_status_changed', label: 'Task Status Changed', category: 'Tasks' },

    // Timesheet Events
    { value: 'timesheet_submitted', label: 'Timesheet Submitted', category: 'Timesheets' },
    { value: 'timesheet_approved', label: 'Timesheet Approved', category: 'Timesheets' },
    { value: 'timesheet_rejected', label: 'Timesheet Rejected', category: 'Timesheets' },
    { value: 'timesheet_due_reminder', label: 'Timesheet Due Reminder', category: 'Timesheets' },

    // Invoice Events
    { value: 'invoice_generated', label: 'Invoice Generated', category: 'Billing' },
    { value: 'invoice_sent', label: 'Invoice Sent', category: 'Billing' },
    { value: 'invoice_paid', label: 'Invoice Paid', category: 'Billing' },
    { value: 'invoice_overdue', label: 'Invoice Overdue', category: 'Billing' },
    { value: 'invoice_partially_paid', label: 'Invoice Partially Paid', category: 'Billing' },

    // Leave Events
    { value: 'leave_applied', label: 'Leave Applied', category: 'Leave Management' },
    { value: 'leave_approved', label: 'Leave Approved', category: 'Leave Management' },
    { value: 'leave_rejected', label: 'Leave Rejected', category: 'Leave Management' },
    { value: 'leave_cancelled', label: 'Leave Cancelled', category: 'Leave Management' },

    // Attendance Events
    { value: 'attendance_missed', label: 'Attendance Missed', category: 'Attendance' },
    { value: 'attendance_late', label: 'Attendance Late', category: 'Attendance' },
    { value: 'attendance_overtime', label: 'Attendance Overtime', category: 'Attendance' },

    // Document Events
    { value: 'document_uploaded', label: 'Document Uploaded', category: 'Documents' },
    { value: 'document_shared', label: 'Document Shared', category: 'Documents' },
    { value: 'document_expired', label: 'Document Expired', category: 'Documents' },

    // System Events
    { value: 'system_maintenance', label: 'System Maintenance', category: 'System' },
    { value: 'backup_completed', label: 'Backup Completed', category: 'System' },
    { value: 'account_locked', label: 'Account Locked', category: 'System' },
    { value: 'password_reset', label: 'Password Reset', category: 'System' },
    { value: 'password_changed', label: 'Password Changed', category: 'System' },
    { value: 'login_suspicious', label: 'Suspicious Login', category: 'System' },

    // User Management Events
    { value: 'user_created', label: 'User Created', category: 'User Management' },
    { value: 'user_created_with_password', label: 'User Created With Password', category: 'User Management' },
    { value: 'user_updated', label: 'User Updated', category: 'User Management' },

    // Payment Events
    { value: 'payment_received', label: 'Payment Received', category: 'Payments' },
    { value: 'payment_failed', label: 'Payment Failed', category: 'Payments' },

    // Client Events
    { value: 'client_onboarded', label: 'Client Onboarded', category: 'Client Management' },
    { value: 'client_updated', label: 'Client Updated', category: 'Client Management' },

    // Report Events
    { value: 'report_generated', label: 'Report Generated', category: 'Reports' },
    { value: 'report_scheduled', label: 'Report Scheduled', category: 'Reports' },
  ];

  const channels = [
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'sms', label: 'SMS', icon: Send },
    { value: 'inApp', label: 'In-App', icon: AlertCircle },
    { value: 'push', label: 'Push', icon: Send }
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notification-templates?limit=100');
      console.log('Fetch templates response:', response.data);

      const templatesData = response.data.data?.templates || response.data?.templates || [];
      // Ensure we have a valid array with valid template objects
      const validTemplates = Array.isArray(templatesData) 
        ? templatesData.filter(template => template && typeof template === 'object' && template.name)
        : [];
      
      setTemplates(validTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch notification templates');
      setTemplates([]); // Ensure we always have a valid array
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVariables = async (eventType: string) => {
    try {
      const response = await api.get(`/notification-templates/variables/${eventType}`);
      setAvailableVariables(response.data.data?.variables || []);
    } catch (error) {
      console.error('Error fetching variables:', error);
      setAvailableVariables([]);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      // Validate required fields before sending
      if (!templateForm.eventType) {
        toast.error('Please select an event type');
        return;
      }
      if (!templateForm.name.trim()) {
        toast.error('Please enter a template name');
        return;
      }
      if (!templateForm.content.body.trim()) {
        toast.error('Please enter template content');
        return;
      }
      if (templateForm.channel === 'email' && !templateForm.content.subject?.trim()) {
        toast.error('Please enter email subject');
        return;
      }

      console.log('Sending template data:', JSON.stringify(templateForm, null, 2));
      const response = await api.post('/notification-templates', templateForm);
      console.log('Create response:', response.data);
      
      // Handle different response structures
      const newTemplate = response.data?.data?.template || response.data?.template || response.data?.data;
      
      if (newTemplate && newTemplate.id) {
        toast.success('Template created successfully');
        setTemplates([...templates, newTemplate]);
        resetForm();
        setShowModal(false);
      } else {
        console.error('Invalid response structure:', response.data);
        toast.error('Template created but response format is invalid');
        console.log('Expected template object with id, got:', newTemplate);
        // Refresh the templates list to get the latest data
        await fetchTemplates();
        resetForm();
        setShowModal(false);
      }
    } catch (error: any) {
      console.error('Error creating template:', error);
      console.error('Error response:', JSON.stringify(error.response?.data, null, 2));
      console.error('Full error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      let errorMessage = 'Failed to create template';
      if (error.response?.data?.error?.message && typeof error.response.data.error.message === 'string') {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message && typeof error.response.data.message === 'string') {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error && typeof error.response.data.error === 'string') {
        errorMessage = error.response.data.error;
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;

    try {
      // Only send fields that are allowed to be updated
      const updateData = {
        name: templateForm.name,
        description: templateForm.description,
        content: templateForm.content,
        isActive: templateForm.isActive
      };

      console.log('Updating template with data:', updateData);
      const response = await api.put(`/notification-templates/${editingTemplate._id}`, updateData);
      console.log('Update response:', response.data);

      if (response.data?.data?.template) {
        toast.success('Template updated successfully');
        setTemplates(templates.map(t => t._id === editingTemplate._id ? response.data.data.template : t));
        resetForm();
        setShowModal(false);
        setEditingTemplate(null);
      } else {
        console.error('Invalid response structure:', response.data);
        toast.error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Error updating template:', error);
      const errorMessage = typeof error.response?.data?.message === 'string' 
        ? error.response.data.message 
        : 'Failed to update template';
      toast.error(errorMessage);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await api.delete(`/notification-templates/${templateId}`);
      toast.success('Template deleted successfully');
      setTemplates(templates.filter(t => t._id !== templateId));
    } catch (error: any) {
      console.error('Error deleting template:', error);
      const errorMessage = typeof error.response?.data?.message === 'string' 
        ? error.response.data.message 
        : 'Failed to delete template';
      toast.error(errorMessage);
    }
  };

  const handleCloneTemplate = async (templateId: string) => {
    try {
      const response = await api.post(`/notification-templates/${templateId}/clone`);
      toast.success('Template cloned successfully');
      setTemplates([...templates, response.data.template]);
    } catch (error: any) {
      console.error('Error cloning template:', error);
      const errorMessage = typeof error.response?.data?.message === 'string' 
        ? error.response.data.message 
        : 'Failed to clone template';
      toast.error(errorMessage);
    }
  };

  const createDefaultTemplates = async () => {
    try {
      setLoading(true);
      await api.post('/notification-templates/create-defaults-firm');
      toast.success('Default templates created successfully');
      await fetchTemplates();
    } catch (error: any) {
      console.error('Error creating default templates:', error);
      const errorMessage = typeof error.response?.data?.message === 'string' 
        ? error.response.data.message 
        : 'Failed to create default templates';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTemplateForm({
      eventType: '',
      channel: 'email',
      name: '',
      description: '',
      content: {
        subject: '',
        body: '',
        htmlBody: '',
        shortText: ''
      },
      language: 'en',
      isActive: true
    });
    setAvailableVariables([]);
  };

  const openEditModal = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setTemplateForm({
      eventType: template.eventType,
      channel: template.channel,
      name: template.name,
      description: template.description || '',
      content: {
        subject: template.content?.subject || '',
        body: template.content?.body || '',
        htmlBody: template.content?.htmlBody || '',
        shortText: template.content?.shortText || ''
      },
      language: template.language,
      isActive: template.isActive
    });
    fetchAvailableVariables(template.eventType);
    setActiveEmailTab('plain');
    setShowModal(true);
  };

  const openCreateModal = () => {
    resetForm();
    setEditingTemplate(null);
    setActiveEmailTab('plain');
    setShowModal(true);
  };

  const handleEventTypeChange = (eventType: string) => {
    setTemplateForm({ ...templateForm, eventType });
    if (eventType) {
      fetchAvailableVariables(eventType);
    }
  };

  const getRecipientInfo = (eventType: string) => {
    const recipientMap: Record<string, { primary: string[], secondary?: string[], description: string }> = {
      // Task Events
      'task_created': {
        primary: ['Task Assignee'],
        secondary: ['Task Creator', 'Project Manager'],
        description: 'Notifies the assigned user when a new task is created for them'
      },
      'task_assigned': {
        primary: ['Task Assignee'],
        secondary: ['Previous Assignee', 'Task Creator'],
        description: 'Notifies the user when a task is assigned or reassigned to them'
      },
      'task_updated': {
        primary: ['Task Assignee', 'Task Creator'],
        secondary: ['Project Manager', 'Team Members'],
        description: 'Notifies relevant users when task details are modified'
      },
      'task_completed': {
        primary: ['Task Creator', 'Project Manager'],
        secondary: ['Team Members'],
        description: 'Notifies supervisors and stakeholders when a task is completed'
      },
      'task_due_reminder': {
        primary: ['Task Assignee'],
        secondary: ['Task Creator', 'Project Manager'],
        description: 'Reminds the assignee about upcoming task deadlines'
      },

      // Timesheet Events
      'timesheet_submitted': {
        primary: ['Manager', 'HR Admin'],
        secondary: ['Firm Admin'],
        description: 'Notifies managers when an employee submits their timesheet'
      },
      'timesheet_approved': {
        primary: ['Employee (Timesheet Owner)'],
        description: 'Notifies the employee when their timesheet is approved'
      },
      'timesheet_rejected': {
        primary: ['Employee (Timesheet Owner)'],
        description: 'Notifies the employee when their timesheet is rejected with feedback'
      },

      // Leave Events
      'leave_requested': {
        primary: ['Manager', 'HR Admin'],
        secondary: ['Firm Admin'],
        description: 'Notifies managers when an employee requests leave'
      },
      'leave_approved': {
        primary: ['Employee (Leave Requester)'],
        secondary: ['Team Members'],
        description: 'Notifies the employee when their leave request is approved'
      },
      'leave_rejected': {
        primary: ['Employee (Leave Requester)'],
        description: 'Notifies the employee when their leave request is rejected'
      },

      // Invoice Events
      'invoice_created': {
        primary: ['Client'],
        secondary: ['Account Manager', 'Firm Admin'],
        description: 'Notifies the client when a new invoice is generated'
      },
      'invoice_sent': {
        primary: ['Client'],
        secondary: ['Account Manager'],
        description: 'Notifies the client when an invoice is officially sent'
      },
      'invoice_paid': {
        primary: ['Account Manager', 'Firm Admin'],
        secondary: ['Finance Team'],
        description: 'Notifies internal team when an invoice payment is received'
      },
      'invoice_overdue': {
        primary: ['Client'],
        secondary: ['Account Manager', 'Finance Team'],
        description: 'Reminds the client about overdue invoice payments'
      },

      // User Events
      'user_created': {
        primary: ['New User'],
        secondary: ['Manager', 'HR Admin'],
        description: 'Welcomes new users and provides account setup information'
      },
      'user_created_with_password': {
        primary: ['New User'],
        secondary: ['Manager', 'HR Admin'],
        description: 'Sends login credentials to new users created with preset password'
      },
      'password_reset': {
        primary: ['User (Password Reset Requester)'],
        description: 'Provides password reset instructions to the requesting user'
      }
    };

    return recipientMap[eventType] || {
      primary: ['Relevant Users'],
      description: 'Notifies users based on the specific event context'
    };
  };

  const insertVariable = (variableName: string) => {
    const variable = `{{${variableName}}}`;
    if (templateForm.channel === 'email') {
      // Insert into the active tab (plain text or HTML)
      const targetField = activeEmailTab === 'plain' ? 'body' : 'htmlBody';
      setTemplateForm({
        ...templateForm,
        content: {
          ...templateForm.content,
          [targetField]: templateForm.content[targetField] + variable
        }
      });
    } else {
      // For non-email channels, insert into body
      setTemplateForm({
        ...templateForm,
        content: {
          ...templateForm.content,
          body: templateForm.content.body + variable
        }
      });
    }
  };

  return (
    <FirmAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Template Management</h1>
              <p className="text-gray-600">Manage notification templates for task events and communications</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={createDefaultTemplates}
                disabled={loading}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Create Defaults
              </button>
              <button
                onClick={openCreateModal}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </button>
            </div>
          </div>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-600">Loading templates...</span>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="grid gap-4">
                {templates.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
                    <p className="text-gray-600 mb-4">Create your first notification template to get started</p>
                    <button
                      onClick={openCreateModal}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Template
                    </button>
                  </div>
                ) : (
                  templates.filter(template => template && template.name).map((template) => (
                    <div key={template._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-medium text-gray-900">{template.name}</h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              template.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {template.isActive ? 'Active' : 'Inactive'}
                            </span>
                            {template.isDefault && (
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>Event: {template.eventType}</span>
                            <span>Channel: {template.channel}</span>
                            <span>Language: {template.language}</span>
                            <span>Version: {template.version}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(template)}
                            className="p-2 text-gray-400 hover:text-gray-600"
                            title="Edit template"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleCloneTemplate(template._id)}
                            className="p-2 text-gray-400 hover:text-gray-600"
                            title="Clone template"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          {!template.isDefault && (
                            <button
                              onClick={() => handleDeleteTemplate(template._id)}
                              className="p-2 text-gray-400 hover:text-red-600"
                              title="Delete template"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Template Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full h-[95vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {editingTemplate ? 'Edit Template' : 'Create Template'}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 flex-1 overflow-hidden">
                <div className="flex gap-6 h-full">
                  {/* Form Section */}
                  <div className="flex-1 flex flex-col space-y-4 overflow-hidden">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Event Type *
                          {editingTemplate && (
                            <span className="text-xs text-gray-500 ml-2">(Cannot be changed)</span>
                          )}
                        </label>
                        <select
                          value={templateForm.eventType}
                          onChange={(e) => handleEventTypeChange(e.target.value)}
                          disabled={!!editingTemplate}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                            editingTemplate ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          required
                        >
                          <option value="">Select event type</option>
                          {Object.entries(
                            eventTypes.reduce((acc, type) => {
                              if (!acc[type.category]) acc[type.category] = [];
                              acc[type.category].push(type);
                              return acc;
                            }, {} as Record<string, typeof eventTypes>)
                          ).map(([category, types]) => (
                            <optgroup key={category} label={category}>
                              {types.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Channel *
                          {editingTemplate && (
                            <span className="text-xs text-gray-500 ml-2">(Cannot be changed)</span>
                          )}
                        </label>
                        <select
                          value={templateForm.channel}
                          onChange={(e) => setTemplateForm({ ...templateForm, channel: e.target.value })}
                          disabled={!!editingTemplate}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                            editingTemplate ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          required
                        >
                          {channels.map((channel) => (
                            <option key={channel.value} value={channel.value}>
                              {channel.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Template Name *
                      </label>
                      <input
                        type="text"
                        value={templateForm.name}
                        onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter template name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={templateForm.description}
                        onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter template description"
                      />
                    </div>

                    {/* Email Content */}
                    {templateForm.channel === 'email' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Subject *
                          </label>
                          <input
                            type="text"
                            value={templateForm.content?.subject || ''}
                            onChange={(e) => setTemplateForm({
                              ...templateForm,
                              content: { ...templateForm.content, subject: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Enter email subject"
                            required
                          />
                        </div>

                        {/* Email Body Tabs */}
                        <div className="flex-1 flex flex-col min-h-0">
                          <div className="flex border-b border-gray-200 mb-4">
                            <button
                              type="button"
                              onClick={() => setActiveEmailTab('plain')}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                activeEmailTab === 'plain'
                                  ? 'border-primary-500 text-primary-600'
                                  : 'border-transparent text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              Email Body (Plain Text) *
                            </button>
                            <button
                              type="button"
                              onClick={() => setActiveEmailTab('html')}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                activeEmailTab === 'html'
                                  ? 'border-primary-500 text-primary-600'
                                  : 'border-transparent text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              HTML Body (Optional)
                            </button>
                          </div>

                          <div className="flex-1 min-h-0">
                            {activeEmailTab === 'plain' && (
                              <textarea
                                value={templateForm.content?.body || ''}
                                onChange={(e) => setTemplateForm({
                                  ...templateForm,
                                  content: { ...templateForm.content, body: e.target.value }
                                })}
                                className="w-full h-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                placeholder="Enter email body content"
                                required
                              />
                            )}
                            {activeEmailTab === 'html' && (
                              <textarea
                                value={templateForm.content?.htmlBody || ''}
                                onChange={(e) => setTemplateForm({
                                  ...templateForm,
                                  content: { ...templateForm.content, htmlBody: e.target.value }
                                })}
                                className="w-full h-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm resize-none"
                                placeholder="Enter HTML content"
                              />
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* SMS Content */}
                    {templateForm.channel === 'sms' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          SMS Message * (160 characters max)
                        </label>
                        <textarea
                          value={templateForm.content?.shortText || ''}
                          onChange={(e) => setTemplateForm({
                            ...templateForm,
                            content: { ...templateForm.content, shortText: e.target.value }
                          })}
                          rows={3}
                          maxLength={160}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter SMS message"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {templateForm.content.shortText?.length || 0}/160 characters
                        </p>
                      </div>
                    )}

                    {/* Other Channels */}
                    {(templateForm.channel === 'inApp' || templateForm.channel === 'push') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message Content *
                        </label>
                        <textarea
                          value={templateForm.content?.body || ''}
                          onChange={(e) => setTemplateForm({
                            ...templateForm,
                            content: { ...templateForm.content, body: e.target.value }
                          })}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter message content"
                          required
                        />
                      </div>
                    )}

                    {/* Settings */}
                    <div className="flex items-center gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={templateForm.isActive}
                          onChange={(e) => setTemplateForm({ ...templateForm, isActive: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>

                  {/* Variables Section */}
                  <div className="w-80 flex-shrink-0">
                    <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
                      {/* Recipients Section */}
                      {templateForm.eventType && (
                        <div className="mb-4 pb-4 border-b border-gray-200">
                          <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                            <Users className="w-4 h-4 mr-2" />
                            Email Recipients
                          </h3>
                          {(() => {
                            const recipientInfo = getRecipientInfo(templateForm.eventType);
                            return (
                              <div className="space-y-2">
                                <div>
                                  <span className="text-xs font-medium text-gray-700">Primary Recipients:</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {recipientInfo.primary.map((recipient, index) => (
                                      <span
                                        key={index}
                                        className="inline-block px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded"
                                      >
                                        {recipient}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                {recipientInfo.secondary && recipientInfo.secondary.length > 0 && (
                                  <div>
                                    <span className="text-xs font-medium text-gray-700">Secondary Recipients:</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {recipientInfo.secondary.map((recipient, index) => (
                                        <span
                                          key={index}
                                          className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                                        >
                                          {recipient}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <p className="text-xs text-gray-600 mt-2">
                                  {recipientInfo.description}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                        <Code className="w-4 h-4 mr-2" />
                        Available Variables
                      </h3>
                      {availableVariables.length > 0 ? (
                        <div className="flex-1 overflow-y-auto">
                          <div className="space-y-2">
                            {availableVariables.map((variable) => (
                              <div key={variable.name} className="border border-gray-200 rounded p-2 bg-white">
                                <div className="flex items-center justify-between">
                                  <code className="text-sm text-primary-600">
                                    {`{{${variable.name}}}`}
                                  </code>
                                  <button
                                    onClick={() => insertVariable(variable.name)}
                                    className="text-xs text-gray-500 hover:text-primary-600"
                                    title={`Insert into ${activeEmailTab === 'plain' ? 'Plain Text' : 'HTML'} body`}
                                  >
                                    Insert
                                  </button>
                                </div>
                                <p className="text-xs text-gray-600 mt-1">{variable.description}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-gray-500">Type: {variable.type}</span>
                                  {variable.required && (
                                    <span className="text-xs text-red-500">Required</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 flex items-center justify-center">
                          <p className="text-sm text-gray-500 text-center">
                            Select an event type to see available variables
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
                  disabled={!templateForm.eventType || !templateForm.name || !templateForm.content?.body}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </FirmAdminLayout>
  );
};
